import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LoginForm from './LoginForm/LoginForm';
import ForgotPasswordForm from './ForgotPasswordForm/ForgotPasswordForm';
import './Authorization.css';
import { useAuth } from '../../context/AuthContext';
import { ErrorMessageContext } from '../../context/ErrorMessageContext';
import { API } from '../../services';
import {
  sanitizeInput,
  getErrorMessage,
} from '../../utilities/commonFunctions';
import { useTheme } from '../../context/ThemeContext';
import {
  DARK,
  LIGHT,
  LOGIN_TYPE_KEYS,
  LOGIN_TYPE_MESSAGES,
  USER_ROLE_KEYS,
} from '../../utilities/constants';
import OTPVerificationForm from './OTPVerificationForm/OTPVerificationForm';
import { useApiLinks } from '../../context/ApiLinksContext';
import { useUserRolesAndPermissions } from '../../context/UserRolesAndPermissionsContext';
import PageLayout from './RegistrationForm/PageLayout';
import RegistrationForm from './RegistrationForm/RegistrationForm';
import BoschLogoSvg from '../../assets/bosch-logo-authorization.svg';

import { Typography } from 'antd';

const { Text } = Typography;

const loginTypes = {
  DEFAULT_LOGIN: 'default-login',
  DEVICE_REGISTRATION_LOGIN: 'device-registration-login',
};

const AuthPage = () => {
  const location = useLocation();
  const formType = location.pathname.split('/')[1];
  const [loginType, setLoginType] = useState(loginTypes.DEFAULT_LOGIN);
  const { toggleTheme } = useTheme();
  const { login, setIsLoggedIn, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const { addErrorMessage } = useContext(ErrorMessageContext);
  const [email, setEmail] = useState('');
  const { fetchLinks } = useApiLinks();
  const {
    fetchUserRolesAndPermissions,
    getUserDetails,
    fetchOrganisationFeatures,
    userRole,
  } = useUserRolesAndPermissions();
  const [redirectUrl, setRedirectUrl] = useState(null);
  const [stepType, setStepType] = useState('initial');

  const handleDefaultNavigate = () => {
    if (formType === 'device-registration-login') {
      navigate('/mobile/device-management');
    } else if (userRole === USER_ROLE_KEYS.SUPER_ADMIN) {
      navigate('/super-admin-console');
    } else if (userRole === USER_ROLE_KEYS.PLATFORM_OPERATION_MANAGER) {
      navigate('/platform-operations-console');
    } else {
      navigate('/overview');
    }
  };

  useEffect(() => {
    isLoggedIn && handleDefaultNavigate();
  }, [isLoggedIn, userRole]);

  useEffect(() => {
    if (formType === 'login' && location.state?.stepType) {
      setStepType(location.state.stepType);
      setEmail(location.state.email || '');
    }
  }, [formType, location.state]);

  const handleBack = () => {
    setStepType('initial');
  };

  const renderForm = () => {
    switch (formType) {
      case 'login':
        return (
          <LoginForm
            onLogin={handleMultiStepLogin}
            stepType={stepType}
            handleBack={handleBack}
            showBackButton={stepType === 'initial' ? false : true}
            email={email}
          />
        );
      case 'device-registration-login':
        return (
          <LoginForm
            onLogin={handleMultiStepLogin}
            isDeviceRegistrationLogin={true}
            setLoginType={setLoginType}
            stepType={stepType}
            handleBack={handleBack}
            showBackButton={stepType === 'initial' ? false : true}
            email={email}
          />
        );
      case 'forgot-password':
        return (
          <ForgotPasswordForm
            onReset={handleResetPassword}
            onSendOTP={onSendOTP}
            isMobileLogin={loginType === loginTypes.DEVICE_REGISTRATION_LOGIN}
            email={email}
          />
        );
      case 'otp-verification':
        return (
          <OTPVerificationForm
            onValidateOTP={handleValidateMFAOTP}
            onResendOTP={handleMFALogin}
          />
        );
      case 'complete-registration':
        return <RegistrationForm onHandleRegistration={handleRegistration} />;
      default:
        return (
          <LoginForm
            onLogin={handleMultiStepLogin}
            stepType={stepType}
            handleBack={handleBack}
            showBackButton={stepType === 'initial' ? false : true}
            email={email}
          />
        );
    }
  };

  const handleMultiStepLogin = (email, password) => {
    if (stepType === 'initial') {
      return checkLoginFeature(email);
    } else if (stepType === LOGIN_TYPE_KEYS.SSO) {
      //  redirect to sso page
      window.location.href = redirectUrl;
    } else if (stepType === LOGIN_TYPE_KEYS.MFA) {
      return handleMFALogin(email, password);
    } else if (stepType === LOGIN_TYPE_KEYS.USER_PASSWORD) {
      return handleLogin(email, password);
    }
  };

  const checkLoginFeature = email => {
    return new Promise((resolve, reject) => {
      (async () => {
        try {
          const { data, status } = await API.getLoginFeature({ email });
          if (status === 200) {
            const message = LOGIN_TYPE_MESSAGES[data?.login_feature_list?.[0]];
            addErrorMessage(message, 'success');
            setEmail(email);
            setStepType(data?.login_feature_list?.[0]);
            if (data?.login_feature_list?.[0] === LOGIN_TYPE_KEYS.SSO) {
              setRedirectUrl(data?.redirect_url);
            }
          }
          resolve('step successful!');
        } catch (error) {
          addErrorMessage(
            getErrorMessage(
              error,
              'Unable to process your request! Please try again after sometime.'
            )
          );
          reject(error);
        }
      })();
    });
  };

  const handleLogin = async (email, password) => {
    return new Promise((resolve, reject) => {
      (async () => {
        try {
          const urlEncodedData = new URLSearchParams();
          urlEncodedData.append('email', sanitizeInput(email));
          urlEncodedData.append('password', sanitizeInput(password));
          const {
            data: { access_token, refresh_token, name, access_expires_in },
          } = await API.login(urlEncodedData);
          login({
            access_token,
            refresh_token,
            name,
            email,
            access_expires_in,
          });
          await fetchLinks();
          await fetchUserRolesAndPermissions();
          await fetchOrganisationFeatures();
          await getUserDetails();
          setIsLoggedIn(true);
          loginType === loginTypes.DEVICE_REGISTRATION_LOGIN
            ? toggleTheme(LIGHT, { persist: false })
            : toggleTheme(DARK);

          resolve('login successful!');
        } catch (error) {
          addErrorMessage(
            getErrorMessage(
              error,
              'Unable to login! Please try again after sometime.'
            )
          );
          reject(new Error('login failed!'));
        }
      })();
    });
  };

  const handleMFALogin = async (email, password) => {
    return new Promise((resolve, reject) => {
      (async () => {
        try {
          const formData = new FormData();
          formData.append('email', sanitizeInput(email));
          formData.append('password', sanitizeInput(password));
          const {
            data: { message, phone_number },
          } = await API.loginMFA(formData);
          navigate('/otp-verification', {
            state: {
              email: email,
              password: password,
              phone_number: phone_number,
            },
          });
          addErrorMessage(
            message || 'OTP sent to your registered mobile number.',
            'success'
          );
          resolve(message);
        } catch (error) {
          addErrorMessage(
            getErrorMessage(
              error,
              'Unable to generate OTP! Please try again after sometime.'
            )
          );
          reject(new Error('login failed!'));
        }
      })();
    });
  };

  const handleValidateMFAOTP = async (email, password, otp) => {
    return new Promise((resolve, reject) => {
      (async () => {
        try {
          const formData = new FormData();
          formData.append('email', sanitizeInput(email));
          formData.append('password', sanitizeInput(password));
          formData.append('otp', sanitizeInput(otp));
          const {
            data: { access_token, refresh_token, name, access_expires_in },
          } = await API.validateMFAOTP(formData);
          login({
            access_token,
            refresh_token,
            name,
            email,
            access_expires_in,
          });
          await fetchLinks();
          await fetchUserRolesAndPermissions();
          await fetchOrganisationFeatures();
          await getUserDetails();

          setIsLoggedIn(true);
          loginType === loginTypes.DEVICE_REGISTRATION_LOGIN
            ? toggleTheme(LIGHT, { persist: false })
            : toggleTheme(DARK);
          resolve('login successful!');
        } catch (error) {
          addErrorMessage(
            getErrorMessage(
              error,
              'Unable to validate OTP! Please try again after sometime.'
            )
          );
          reject(new Error('login failed!'));
        }
      })();
    });
  };

  const handleRegistration = async (payload, token, role) => {
    return new Promise((resolve, reject) => {
      (async () => {
        try {
          const urlEncodedData = new URLSearchParams();
          urlEncodedData.append('old_password', token);
          urlEncodedData.append(
            'new_password',
            sanitizeInput(payload?.new_password)
          );
          urlEncodedData.append(
            'confirm_password',
            sanitizeInput(payload?.confirm_password)
          );
          if (role === 'admin') {
            urlEncodedData.append(
              'first_name',
              sanitizeInput(payload?.first_name)
            );
            urlEncodedData.append(
              'last_name',
              sanitizeInput(payload?.last_name)
            );
            urlEncodedData.append(
              'phone_number',
              sanitizeInput(payload?.phone_number)
            );
          }
          const { data } = await API.completeRegistration(urlEncodedData);
          if (data) {
            navigate('login');
            resolve('setup successful!');
          }
        } catch (error) {
          addErrorMessage(
            getErrorMessage(
              error,
              'Unable to complete setup! Please try again after sometime.'
            )
          );
          reject(new Error('setup failed'));
        }
      })();
    });
  };

  const handleResetPassword = async data => {
    // eslint-disable-next-line no-console
    console.log('data: ', data);
    return new Promise((resolve, reject) => {
      (async () => {
        try {
          const formData = new FormData();
          formData.append('email', data?.email);
          formData.append('otp', data?.otp);
          formData.append('new_password', data?.new_password);
          formData.append('confirm_password', data?.confirm_password);
          const {
            data: { message },
          } = await API.resetPassword(formData);
          if (message) resolve('Password reset successful!');
        } catch (error) {
          addErrorMessage(
            getErrorMessage(
              error,
              'Unable to reset password! Please try again after sometime.'
            )
          );
          reject(new Error('reset password failed'));
        }
      })();
    });
  };

  const onSendOTP = async email => {
    return new Promise((resolve, reject) => {
      (async () => {
        try {
          const formData = new FormData();
          formData.append('email_id', email);
          const { data } = await API.sendOTP(formData);
          if (data)
            resolve(data?.message || 'OTP sent to your registered email.');
        } catch (error) {
          addErrorMessage(
            getErrorMessage(
              error,
              'Unable to send OTP! Please try again after sometime.'
            )
          );
          reject(new Error('otp sending failed'));
        }
      })();
    });
  };

  return (
    <>
      {formType === 'complete-registration' ? (
        <PageLayout>{renderForm()}</PageLayout>
      ) : (
        <div className="container">
          <Text className="auth-app-title" strong>
            RideBuddy
          </Text>
          <img
            src={BoschLogoSvg}
            alt="Bosch logo"
            className="auth-bosch-logo"
          />
          {renderForm()}
        </div>
      )}
    </>
  );
};

export default React.memo(AuthPage);
